import { alertSubmitLabel, submitAlertChannels, retainAlertChannelSelection, renderAlertOutcomes } from './alert-channels.js';
import { initializePhoneCountry, isPhoneInput } from './alert-phone-country.js';

const DIALOG_SELECTOR = '[data-stock-alert]';
const CONTROLLER_KEY = Symbol.for('sigma.product-stock-alert-controller');
const CLOSE_FALLBACK_DURATION = 240;
const SUBSCRIPTION_STORAGE_PREFIX = 'sigma:stock-alert:parent-product:';
const ACCOUNT_SYNC_STALE_MS = 30_000;
const ACCOUNT_SUBSCRIPTION_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const NOTIFICATION_CANCELLATION_TOKEN_PATTERN = /^v1\.[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\.[1-9][0-9]{0,19}\.[A-Za-z0-9_-]{43}$/i;
const CANCELLATION_TOKEN_PATTERN = /^(?:[A-Za-z0-9_-]{43}|v1\.[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\.[1-9][0-9]{0,19}\.[A-Za-z0-9_-]{43})$/i;

const setButtonLoading = (button, loading) => {
  button?.dispatchEvent(
    new CustomEvent('sigma:button-loading', {
      bubbles: true,
      detail: { loading },
    }),
  );
};

const parseJson = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
};

const parseEquipmentVariants = () => {
  const source = document.querySelector('[data-stock-alert-variants]');

  if (!source) {
    return [];
  }

  try {
    return JSON.parse(source.textContent);
  } catch (error) {
    return [];
  }
};

const initializeStockAlert = (dialog) => {
  let submitting = false;
  const savedOutcomes = new Map();
  let retryChannels = [];
  const triggers = [...document.querySelectorAll(`[aria-controls="${dialog.id}"]`)];
  const form = dialog.querySelector('[data-stock-alert-form]');
  const formView = dialog.querySelector('[data-stock-alert-form-view]');
  const successView = dialog.querySelector('[data-stock-alert-success]');
  const successTitle = dialog.querySelector('[data-stock-alert-success-title]');
  const successMessage = dialog.querySelector('[data-stock-alert-success-message]');
  const cancelView = dialog.querySelector('[data-stock-alert-cancel-view]');
  const cancelTitle = dialog.querySelector('[data-stock-alert-cancel-title]');
  const cancelDescription = dialog.querySelector('[data-stock-alert-cancel-description]');
  const cancelPrompt = dialog.querySelector('[data-stock-alert-cancel-prompt]');
  const cancelConfirmation = dialog.querySelector('[data-stock-alert-cancel-confirmation]');
  const cancelledTitle = dialog.querySelector('[data-stock-alert-cancelled-title]');
  const cancelledDescription = dialog.querySelector('[data-stock-alert-cancelled-description]');
  const cancelForm = dialog.querySelector('[data-stock-alert-cancel-form]');
  const cancelStatus = dialog.querySelector('[data-stock-alert-cancel-status]');
  const cancellationTokenInput = dialog.querySelector('[data-stock-alert-cancellation-token]');
  const cancelSubmit = dialog.querySelector('[data-stock-alert-cancel-submit]');
  const cancelSubmitLabel = dialog.querySelector('[data-stock-alert-cancel-submit-label]');
  const managedChannels = dialog.querySelector('[data-stock-alert-managed-channels]');
  const addChannel = dialog.querySelector('[data-stock-alert-add-channel]');
  const addChannelLabel = dialog.querySelector('[data-stock-alert-add-channel-label]');
  const keepLabel = dialog.querySelector('[data-stock-alert-keep-label]');
  const channelInputs = [...dialog.querySelectorAll('[data-stock-alert-channel]')];
  const emailField = dialog.querySelector('[data-stock-alert-email-field]');
  const phoneField = dialog.querySelector('[data-stock-alert-phone-field]');
  const emailInput = dialog.querySelector('[data-stock-alert-email]');
  const phoneInput = dialog.querySelector('[data-stock-alert-phone]');
  const countryInput = dialog.querySelector('[data-alert-phone-country]');
  const phoneCountry = initializePhoneCountry(phoneField);
  const privacyRegion = dialog.querySelector('[data-stock-alert-privacy-region]');
  const emailPrivacy = dialog.querySelector('[data-stock-alert-email-privacy]');
  const smsPrivacy = dialog.querySelector('[data-stock-alert-sms-privacy]');
  const variantInput = dialog.querySelector('[data-stock-alert-variant-id]');
  const productInput = form.querySelector('input[name="product_id"]');
  const status = dialog.querySelector('[data-stock-alert-status]');
  const submit = dialog.querySelector('[data-stock-alert-submit]');
  const submitLabel = dialog.querySelector('[data-stock-alert-submit-label]');
  const card = dialog.querySelector('.product-stock-alert__card');
  const originalLabelledBy = dialog.getAttribute('aria-labelledby');
  const originalDescribedBy = dialog.getAttribute('aria-describedby');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const coarsePointer = window.matchMedia('(pointer: coarse)');
  const accountSyncEnabled = dialog.dataset.stockAlertAccountSync === 'true';
  const accountStatusUrl = dialog.dataset.stockAlertAccountStatusUrl || '';
  const accountLinkUrl = dialog.dataset.stockAlertAccountLinkUrl || '';
  const accountCancelUrl = dialog.dataset.stockAlertAccountCancelUrl || '';
  let activeTrigger;
  let closeTimeoutId;
  let backdropAnimation;
  let resizeFrameId;
  let resizeTimeoutId;
  let accountSyncPromise;
  let lastAccountSyncAt = 0;
  let selectedVariantAvailable = triggers.length > 0 && triggers.every((trigger) => trigger.hidden);
  const triggerLabels = new Map(
    triggers.map((trigger) => [
      trigger,
      {
        accessible: trigger.getAttribute('aria-label') || 'Create a stock alert',
        visible: trigger.querySelector('[data-stock-alert-trigger-label]')?.textContent || 'Notify me',
      },
    ]),
  );

  if (!form || triggers.length === 0) {
    return;
  }

  const selectedVariantId = () =>
    document.querySelector('[data-variant-select], [data-stock-alert-variant-select]')?.value ||
    variantInput?.value ||
    '';

  const subscriptionStorageKey = productInput?.value
    ? `${SUBSCRIPTION_STORAGE_PREFIX}${productInput.value}`
    : null;

  const readStoredSubscription = () => {
    if (!subscriptionStorageKey) {
      return null;
    }

    try {
      const stored = window.localStorage.getItem(subscriptionStorageKey);
      if (!stored) {
        return null;
      }
      const parsed = JSON.parse(stored);
      const entries = Array.isArray(parsed?.subscriptions) ? parsed.subscriptions : [parsed];
      const subscriptions = entries.filter((entry) =>
        CANCELLATION_TOKEN_PATTERN.test(entry?.cancellationToken || ''),
      ).map((entry) => ({
        cancellationToken: entry.cancellationToken,
        channel: entry.channel === 'email' || entry.channel === 'sms' ? entry.channel : null,
        destination: typeof entry.destination === 'string' ? entry.destination : '',
      }));
      if (subscriptions.length > 0) {
        return subscriptions;
      }
      window.localStorage.removeItem(subscriptionStorageKey);
      return null;
    } catch (error) {
      return null;
    }
  };

  let currentSubscriptions = readStoredSubscription() || [];
  let managedSubscription = null;

  const hasStoredSubscription = () => currentSubscriptions.length > 0;
  const subscriptionIdentity = (subscription) => (
    subscription?.subscriptionId
      ? `account:${subscription.subscriptionId}`
      : `token:${subscription?.cancellationToken || ''}`
  );

  const persistSubscriptions = () => {
    try {
      if (!subscriptionStorageKey) {
        return;
      }
      const localSubscriptions = currentSubscriptions.filter((subscription) =>
        CANCELLATION_TOKEN_PATTERN.test(subscription?.cancellationToken || ''),
      ).map((subscription) => ({
        cancellationToken: subscription.cancellationToken,
        channel: subscription.channel,
        destination: subscription.destination,
      }));
      if (localSubscriptions.length === 0) {
        window.localStorage.removeItem(subscriptionStorageKey);
        return;
      }
      window.localStorage.setItem(
        subscriptionStorageKey,
        JSON.stringify({ subscriptions: localSubscriptions }),
      );
    } catch (error) {
      // In-memory capabilities still allow cancellation during this page view.
    }
  };

  const importNotificationSubscription = () => {
    const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
    const params = new URLSearchParams(hash);
    const cancellationToken = params.get('stock_alert_token') || '';
    if (!cancellationToken) {
      return;
    }

    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}`,
    );

    const channel = params.get('stock_alert_channel');
    const productId = productInput?.value?.replace('gid://shopify/Product/', '') || '';
    const tokenProductId = cancellationToken.split('.')[2] || '';
    if (
      !NOTIFICATION_CANCELLATION_TOKEN_PATTERN.test(cancellationToken) ||
      (channel !== 'email' && channel !== 'sms') ||
      !productId ||
      tokenProductId !== productId
    ) {
      return;
    }

    const importedSubscription = {
      cancellationToken,
      channel,
      destination: '',
    };
    currentSubscriptions = [
      ...currentSubscriptions.filter((subscription) => subscription.channel !== channel),
      importedSubscription,
    ];
    managedSubscription = importedSubscription;
    persistSubscriptions();
  };

  importNotificationSubscription();

  const maskDestination = (channel, destination) => {
    if (channel === 'sms') {
      return `•••• ${destination.slice(-4)}`;
    }
    const [local = '', domain = ''] = destination.split('@');
    return `${local.slice(0, 1) || '•'}•••@${domain}`;
  };

  const storeSubscription = (channel, destination, cancellationToken) => {
    const next = {
      cancellationToken,
      channel,
      destination: maskDestination(channel, destination),
    };
    currentSubscriptions = [
      ...currentSubscriptions.filter((subscription) => subscription.channel !== channel),
      next,
    ];
    managedSubscription = next;
    persistSubscriptions();
  };

  const clearStoredSubscription = (identity) => {
    currentSubscriptions = currentSubscriptions.filter(
      (subscription) => subscriptionIdentity(subscription) !== identity,
    );
    managedSubscription = currentSubscriptions[0] || null;
    persistSubscriptions();
  };

  const syncSubscribedTriggers = (subscribed = hasStoredSubscription()) => {
    triggers.forEach((trigger) => {
      const label = trigger.querySelector('[data-stock-alert-trigger-label]');
      const defaults = triggerLabels.get(trigger);

      trigger.dataset.stockAlertSubscribed = String(subscribed);
      trigger.setAttribute(
        'aria-label',
        subscribed ? 'Manage alert for this product' : defaults.accessible,
      );
      if (label) {
        label.textContent = subscribed ? 'Alert saved' : defaults.visible;
      }
      trigger.hidden = selectedVariantAvailable && !subscribed;
    });
  };

  const syncManagedSubscription = () => {
    if (!managedSubscription) {
      return;
    }
    const channelLabel = managedSubscription.channel === 'sms'
      ? 'text alert'
      : managedSubscription.channel === 'email'
        ? 'email alert'
        : 'alert';
    if (cancelTitle) {
      cancelTitle.textContent = `Cancel this ${channelLabel}?`;
    }
    if (cancelSubmitLabel) {
      cancelSubmitLabel.textContent = `Cancel ${channelLabel}`;
    }
    if (cancellationTokenInput) {
      cancellationTokenInput.value = managedSubscription.cancellationToken || '';
    }
  };

  const renderManagedSubscriptions = () => {
    if (!managedChannels) {
      return;
    }
    managedChannels.replaceChildren();
    currentSubscriptions.forEach((subscription, index) => {
      const label = document.createElement('label');
      label.className = 'product-stock-alert__managed-channel';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'managed_stock_alert';
      input.checked = subscriptionIdentity(subscription) === subscriptionIdentity(managedSubscription);
      input.addEventListener('change', () => {
        managedSubscription = subscription;
        syncManagedSubscription();
      });
      const copy = document.createElement('span');
      copy.className = 'product-stock-alert__managed-channel-copy';
      const channel = document.createElement('span');
      channel.textContent = subscription.channel === 'sms'
        ? 'Text message'
        : subscription.channel === 'email'
          ? 'Email'
          : `Alert ${index + 1}`;
      const destination = document.createElement('span');
      destination.textContent = subscription.destination || 'Destination saved privately';
      copy.append(channel, destination);
      label.append(input, copy);
      managedChannels.append(label);
    });
    if (addChannel && addChannelLabel) {
      const knownChannels = new Set(currentSubscriptions.map((subscription) => subscription.channel));
      const missingChannel = channelInputs.length > 1 && !knownChannels.has(null)
        ? ['email', 'sms'].find((channel) => !knownChannels.has(channel))
        : null;
      addChannel.hidden = !missingChannel;
      addChannel.dataset.stockAlertChannel = missingChannel || '';
      addChannelLabel.textContent = missingChannel === 'sms' ? 'Add text alert' : 'Add email alert';
    }
    syncManagedSubscription();
  };

  const sameOriginUrl = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin ? url : null;
    } catch (error) {
      return null;
    }
  };

  const accountSubscriptionsFromPayload = (payload) => {
    if (payload?.success !== true || typeof payload.authenticated !== 'boolean') {
      throw new Error('Unable to verify account alert state.');
    }
    if (!payload.authenticated) {
      return null;
    }
    if (!Array.isArray(payload.subscriptions)) {
      throw new Error('Unable to verify account alert state.');
    }

    return payload.subscriptions.map((subscription) => {
      if (
        !ACCOUNT_SUBSCRIPTION_ID_PATTERN.test(subscription?.id || '')
        || (subscription.channel !== 'email' && subscription.channel !== 'sms')
        || typeof subscription.destination !== 'string'
        || subscription.destination.length > 254
      ) {
        throw new Error('Unable to verify account alert state.');
      }
      return {
        subscriptionId: subscription.id,
        channel: subscription.channel,
        destination: subscription.destination,
      };
    });
  };

  const reconcileOpenAccountView = () => {
    if (!dialog.open || !cancelView || cancelView.hidden) {
      return;
    }
    if (currentSubscriptions.length === 0) {
      managedSubscription = null;
      cancelView.hidden = true;
      formView.hidden = false;
      dialog.setAttribute('aria-labelledby', originalLabelledBy);
      dialog.setAttribute('aria-describedby', originalDescribedBy);
      syncChannel({ animate: false });
      resizeFormToContent({ animate: false });
      return;
    }

    const managedIdentity = subscriptionIdentity(managedSubscription);
    managedSubscription = currentSubscriptions.find(
      (subscription) => subscriptionIdentity(subscription) === managedIdentity,
    ) || currentSubscriptions[0];
    renderManagedSubscriptions();
    setMeasuredHeights({
      cardHeight: constrainedCardHeight(cancelView.scrollHeight),
      animate: false,
    });
  };

  const setAccountSyncBusy = (busy) => {
    triggers.forEach((trigger) => {
      if (busy) {
        trigger.setAttribute('aria-busy', 'true');
      } else {
        trigger.removeAttribute('aria-busy');
      }
    });
  };

  const syncAccountSubscriptions = ({ force = false } = {}) => {
    if (!accountSyncEnabled || !productInput) {
      return Promise.resolve(false);
    }
    if (accountSyncPromise) {
      return accountSyncPromise;
    }
    if (!force && Date.now() - lastAccountSyncAt < ACCOUNT_SYNC_STALE_MS) {
      return Promise.resolve(true);
    }

    accountSyncPromise = (async () => {
      const localTokens = currentSubscriptions
        .map((subscription) => subscription.cancellationToken)
        .filter((token) => CANCELLATION_TOKEN_PATTERN.test(token || ''));
      const endpoint = sameOriginUrl(localTokens.length > 0 ? accountLinkUrl : accountStatusUrl);
      if (!endpoint) return false;

      let response;
      if (localTokens.length > 0) {
        response = await fetch(endpoint.toString(), {
          method: 'POST',
          body: JSON.stringify({
            product_id: productInput.value,
            cancellation_tokens: localTokens,
          }),
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });
      } else {
        endpoint.searchParams.set('product_id', productInput.value);
        response = await fetch(endpoint.toString(), {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
      }

      const payload = await parseJson(response);
      if (!response.ok || payload.success !== true) {
        throw new Error(payload.message || 'Unable to refresh account alerts.');
      }

      const accountSubscriptions = accountSubscriptionsFromPayload(payload);
      currentSubscriptions = accountSubscriptions ?? readStoredSubscription() ?? [];
      managedSubscription = currentSubscriptions[0] || null;
      persistSubscriptions();
      lastAccountSyncAt = Date.now();
      syncSubscribedTriggers();
      reconcileOpenAccountView();
      return true;
    })().catch(() => false).finally(() => {
      accountSyncPromise = undefined;
      setAccountSyncBusy(false);
    });
    setAccountSyncBusy(true);
    return accountSyncPromise;
  };

  const syncVariant = () => {
    if (variantInput) {
      variantInput.value = selectedVariantId();
    }
  };

  const selectedChannels = () => channelInputs.length ? channelInputs.filter(input => input.checked).map(input => input.value) : ['email'];

  const pixels = (value) => Number.parseFloat(value) || 0;

  const cardChromeHeight = () => {
    const styles = window.getComputedStyle(card);

    return (
      pixels(styles.paddingTop) +
      pixels(styles.paddingBottom) +
      pixels(styles.borderTopWidth) +
      pixels(styles.borderBottomWidth)
    );
  };

  const constrainedCardHeight = (contentHeight) => {
    const maxHeight = pixels(window.getComputedStyle(card).maxHeight);
    const targetHeight = contentHeight + cardChromeHeight();

    return maxHeight > 0 ? Math.min(targetHeight, maxHeight) : targetHeight;
  };

  const finishResize = () => {
    window.cancelAnimationFrame(resizeFrameId);
    window.clearTimeout(resizeTimeoutId);
    resizeFrameId = undefined;
    resizeTimeoutId = undefined;
    delete dialog.dataset.stockAlertResizing;
  };

  const setMeasuredHeights = ({ cardHeight, privacyHeight, animate }) => {
    if (!card || !dialog.open) {
      return;
    }

    const currentCardHeight = card.getBoundingClientRect().height;
    const currentPrivacyHeight = privacyRegion?.getBoundingClientRect().height || 0;
    finishResize();
    card.style.height = `${currentCardHeight}px`;

    if (privacyRegion && privacyHeight != null) {
      privacyRegion.style.height = `${currentPrivacyHeight}px`;
    }

    card.offsetHeight;

    if (animate && !reducedMotion.matches) {
      dialog.dataset.stockAlertResizing = 'true';
    }

    const applyTargets = () => {
      card.style.height = `${cardHeight}px`;
      if (privacyRegion && privacyHeight != null) {
        privacyRegion.style.height = `${privacyHeight}px`;
      }
    };

    if (animate && !reducedMotion.matches) {
      resizeFrameId = window.requestAnimationFrame(applyTargets);
      resizeTimeoutId = window.setTimeout(finishResize, 260);
    } else {
      applyTargets();
    }
  };

  const resizeFormToContent = ({ animate = true } = {}) => {
    if (!dialog.open || !formView || formView.hidden) {
      return;
    }

    const disclosures = [emailPrivacy, smsPrivacy].filter(element => element && !element.hidden);
    const privacyGap = privacyRegion ? pixels(window.getComputedStyle(privacyRegion).rowGap) : 0;
    const privacyHeight = disclosures.reduce((height, element) => height + element.scrollHeight, 0)
      + Math.max(0, disclosures.length - 1) * privacyGap;
    // The region keeps an explicit pixel height throughout the resize. Measure
    // both disclosures and replace its current height in the form measurement.
    const contentHeight = formView.offsetHeight - (privacyRegion?.offsetHeight || 0) + privacyHeight;
    setMeasuredHeights({ cardHeight: constrainedCardHeight(contentHeight), privacyHeight, animate });
  };

  const syncChannel = ({ animate = dialog.open, clearStatus = true } = {}) => {
    const selected = selectedChannels();
    const isSms = selected.includes('sms');
    const isEmail = selected.includes('email');
    if (emailField) emailField.hidden = !isEmail;
    if (emailInput) emailInput.disabled = submitting || !isEmail;
    if (phoneField) phoneField.hidden = !isSms;
    if (phoneInput) phoneInput.disabled = submitting || !isSms;
    if (countryInput) countryInput.disabled = submitting || !isSms || !phoneCountry.ready();
    if (isSms && clearStatus && !phoneCountry.ready()) void phoneCountry.refresh().then(() => {
      if (countryInput) countryInput.disabled = submitting || !selectedChannels().includes('sms') || !phoneCountry.ready();
      resizeFormToContent();
    });
    if (emailPrivacy) { emailPrivacy.hidden = !isEmail; emailPrivacy.setAttribute('aria-hidden', String(!isEmail)); }
    if (smsPrivacy) { smsPrivacy.hidden = !isSms; smsPrivacy.setAttribute('aria-hidden', String(!isSms)); }
    if (submitLabel && channelInputs.length) submitLabel.textContent = alertSubmitLabel(selected, selected.length > 0 && selected.every(channel => retryChannels.includes(channel)));
    if (submit && channelInputs.length) submit.dataset.buttonLoadingText = selected.length === 2 ? 'Creating alerts...' : isSms ? 'Creating text alert...' : 'Creating email alert...';
    if (clearStatus && !status.querySelector('.alert-results')) { status.hidden = true; status.textContent = ''; }
    resizeFormToContent({ animate });
  };

  const reset = () => {
    form.reset();
    savedOutcomes.clear();
    retryChannels = [];
    status.hidden = true;
    status.textContent = '';
    formView.hidden = false;
    successView.hidden = true;
    if (cancelView) {
      cancelView.hidden = true;
      delete cancelView.dataset.stockAlertCancelled;
    }
    if (cancelTitle) {
      cancelTitle.textContent = 'Cancel this alert?';
    }
    if (cancelDescription) {
      cancelDescription.hidden = false;
    }
    if (cancelPrompt) {
      cancelPrompt.hidden = false;
    }
    if (cancelConfirmation) {
      cancelConfirmation.hidden = true;
    }
    if (cancelStatus) {
      cancelStatus.hidden = true;
      cancelStatus.textContent = '';
    }
    if (keepLabel) {
      keepLabel.textContent = 'Keep alert';
    }
    managedSubscription = null;
    setButtonLoading(cancelSubmit, false);
    dialog.setAttribute('aria-labelledby', originalLabelledBy);
    dialog.setAttribute('aria-describedby', originalDescribedBy);
    setButtonLoading(submit, false);
    syncVariant();
    syncChannel();
  };

  const onClosed = () => {
    if (dialog.open) return;
    window.clearTimeout(closeTimeoutId);
    closeTimeoutId = undefined;
    finishResize();
    delete dialog.dataset.stockAlertState;
    triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    if (!submitting) reset();
    activeTrigger?.focus();
    activeTrigger = undefined;
  };

  const finalizeClose = () => {
    window.clearTimeout(closeTimeoutId);
    closeTimeoutId = undefined;

    if (dialog.open) {
      dialog.close();
      // Synchronize immediately; native close events can be delivered later.
      onClosed();
    }
  };

  const close = () => {
    if (!dialog.open || dialog.dataset.stockAlertState === 'closing') {
      return;
    }

    finishResize();

    if (reducedMotion.matches || !card) {
      finalizeClose();
      return;
    }

    const finishOnOpacity = (event) => {
      if (event.target !== card || event.propertyName !== 'opacity') {
        return;
      }

      card.removeEventListener('transitionend', finishOnOpacity);
      finalizeClose();
    };

    dialog.dataset.stockAlertState = 'closing';
    backdropAnimation?.cancel();
    try {
      backdropAnimation = dialog.animate(
        [{ opacity: 1 }, { opacity: 0 }],
        {
          duration: 160,
          easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
          fill: 'both',
          pseudoElement: '::backdrop',
        },
      );
    } catch {
      backdropAnimation = undefined;
    }
    card.addEventListener('transitionend', finishOnOpacity);
    closeTimeoutId = window.setTimeout(() => {
      card.removeEventListener('transitionend', finishOnOpacity);
      finalizeClose();
    }, CLOSE_FALLBACK_DURATION);
  };

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', async () => {
      if (dialog.open) {
        return;
      }
      activeTrigger = trigger;
      await syncAccountSubscriptions();
      syncVariant();
      trigger.setAttribute('aria-expanded', 'true');
      dialog.dataset.stockAlertState = 'opening';
      const isSubscribed = hasStoredSubscription();
      if (isSubscribed && cancelView && cancelTitle && cancellationTokenInput) {
        managedSubscription = currentSubscriptions[0];
        formView.hidden = true;
        successView.hidden = true;
        cancelView.hidden = false;
        renderManagedSubscriptions();
        dialog.setAttribute('aria-labelledby', cancelTitle.id);
        dialog.setAttribute('aria-describedby', cancelDescription.id);
      }
      dialog.showModal();
      if (!reducedMotion.matches) {
        try {
          backdropAnimation?.cancel();
          const animation = dialog.animate(
            [{ opacity: 0 }, { opacity: 1 }],
            {
              duration: 220,
              easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
              fill: 'both',
              pseudoElement: '::backdrop',
            },
          );
          backdropAnimation = animation;
          animation.finished
            .then(() => {
              if (backdropAnimation === animation) {
                animation.cancel();
                backdropAnimation = undefined;
              }
            })
            .catch(() => {});
        } catch {
          backdropAnimation = undefined;
        }
      }
      if (!isSubscribed) {
        syncChannel({ animate: false });
      }
      if (isSubscribed) {
        cancelTitle?.focus({ preventScroll: true });
      } else if (coarsePointer.matches || navigator.maxTouchPoints > 0) {
        card?.focus({ preventScroll: true });
      } else {
        emailInput?.focus({ preventScroll: true });
      }
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (dialog.open && dialog.dataset.stockAlertState === 'opening') {
            dialog.dataset.stockAlertState = 'open';
          }
        });
      });
    });
  });

  dialog.querySelectorAll('[data-stock-alert-close]').forEach((control) => {
    control.addEventListener('click', close);
  });

  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });

  dialog.addEventListener('close', onClosed);

  channelInputs.forEach((input) => {
    input.addEventListener('change', () => {
      retainAlertChannelSelection(channelInputs, input);
      syncChannel({ animate: true });
    });
  });
  form.addEventListener('sigma:alert-phone-resize', () => resizeFormToContent());
  addChannel?.addEventListener('click', () => {
    const channel = addChannel.dataset.stockAlertChannel;
    const input = channelInputs.find((candidate) => candidate.value === channel);
    if (!input || !formView || !cancelView) {
      return;
    }
    channelInputs.forEach(candidate => { candidate.checked = candidate === input; });
    cancelView.hidden = true;
    formView.hidden = false;
    dialog.setAttribute('aria-labelledby', originalLabelledBy);
    dialog.setAttribute('aria-describedby', originalDescribedBy);
    syncChannel({ animate: false });
    const destinationInput = channel === 'sms' ? phoneInput : emailInput;
    destinationInput?.focus({ preventScroll: true });
  });
  const channelFieldset = dialog.querySelector('[data-stock-alert-channels]');
  const channelFallback = dialog.querySelector('[data-stock-alert-channel-fallback]');
  if (channelFieldset) { channelFieldset.disabled = false; channelFieldset.hidden = false; }
  if (channelFallback) channelFallback.disabled = true;
  dialog.dataset.stockAlertEnhanced = 'true';
  syncChannel();

  const syncOpenViewHeight = () => {
    if (!dialog.open || dialog.dataset.modalKeyboard === 'true') {
      return;
    }

    window.requestAnimationFrame(() => {
      if (!dialog.open || dialog.dataset.modalKeyboard === 'true') {
        return;
      }

      if (cancelView && !cancelView.hidden) {
        setMeasuredHeights({
          cardHeight: constrainedCardHeight(cancelView.scrollHeight),
          animate: false,
        });
        return;
      }

      if (!successView.hidden) {
        setMeasuredHeights({
          cardHeight: constrainedCardHeight(successView.scrollHeight),
          animate: false,
        });
        return;
      }

      syncChannel({ animate: false, clearStatus: false });
    });
  };

  window.addEventListener('resize', syncOpenViewHeight);
  let previousViewportKey;
  dialog.addEventListener('sigma:modal-viewport-change', (event) => {
    // Focus changes re-emit viewport events even when geometry is unchanged.
    // Do not let those events interrupt a channel resize with an instant snap.
    const key = `${dialog.clientHeight}:${card.clientWidth}:${dialog.dataset.modalKeyboard || ''}`;
    if (key === previousViewportKey) return;
    previousViewportKey = key;
    if (event.detail?.keyboardOpen) {
      finishResize();
      return;
    }

    syncOpenViewHeight();
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (submitting) return;
    if (!form.reportValidity()) {
      return;
    }

    const hadVisibleStatus = !status.hidden;
    status.hidden = true;
    status.textContent = '';
    if (hadVisibleStatus) {
      resizeFormToContent();
    }
    const selected = selectedChannels();
    if (!selected.length) {
      status.textContent = 'Choose email, text message, or both.';
      status.hidden = false;
      resizeFormToContent();
      channelInputs[0]?.focus();
      return;
    }
    if (selected.includes('sms') && (!phoneCountry.ready() || !isPhoneInput(phoneInput?.value))) {
      status.textContent = !phoneCountry.ready() ? 'Retry loading countries or choose email.' : 'Check the mobile number and country.';
      status.hidden = false;
      phoneInput?.setAttribute('aria-invalid', 'true');
      resizeFormToContent();
      phoneInput?.focus();
      return;
    }
    phoneInput?.removeAttribute('aria-invalid');
    // Snapshot both requests before disabling controls. Each contains only its own destination.
    const requests = selected.map(channel => {
      const body = new FormData(form);
      body.set('channel', channel);
      if (channel === 'email') { body.delete('phone_number'); body.delete('phone_country'); }
      else body.delete('email');
      return { channel, body, destination: channel === 'sms' ? phoneInput.value : emailInput.value };
    });
    submitting = true;
    channelInputs.forEach(input => { input.disabled = true; });
    syncChannel({ clearStatus: false });
    submit.disabled = true;
    form.setAttribute('aria-busy', 'true');
    setButtonLoading(submit, true);
    try {
      const outcomes = await submitAlertChannels(requests, async ({ channel, body, destination }) => {
        const response = await fetch(form.action, {
          method: 'POST', body, credentials: 'same-origin', cache: 'no-store', redirect: 'error',
          headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(35000),
        });
        const payload = await parseJson(response);
        if (!response.ok || payload.success !== true) throw new Error(payload.message || 'Please try again.');
        if (!CANCELLATION_TOKEN_PATTERN.test(payload.cancellation_token || '')) throw new Error('Your alert may be saved, but management is unavailable. Please try again.');
        const normalized = channel === 'sms' ? payload.phone_number : payload.email;
        storeSubscription(channel, normalized || destination, payload.cancellation_token);
        return payload;
      });
      outcomes.filter(outcome => outcome.ok).forEach(outcome => savedOutcomes.set(outcome.channel, outcome));
      const saved = outcomes.filter(outcome => outcome.ok);
      const failed = outcomes.filter(outcome => !outcome.ok);
      retryChannels = failed.map(outcome => outcome.channel);
      if (saved.length) { syncSubscribedTriggers(true); void syncAccountSubscriptions({ force: true }); }
      if (failed.length) {
        // Successful channels are not included in an explicit retry.
        channelInputs.forEach(input => { input.checked = failed.some(outcome => outcome.channel === input.value); });
        renderAlertOutcomes(status, [...savedOutcomes.values(), ...failed]);
        status.hidden = false;
        if (dialog.open) status.focus();
      } else {
        successTitle.textContent = savedOutcomes.size > 1 ? 'Alert requests saved' : 'Alert request saved';
        renderAlertOutcomes(successMessage, [...savedOutcomes.values()]);
        formView.hidden = true;
        successView.hidden = false;
        setMeasuredHeights({ cardHeight: constrainedCardHeight(successView.scrollHeight), animate: true });
        dialog.setAttribute('aria-labelledby', successTitle.id);
        dialog.setAttribute('aria-describedby', successMessage.id);
        if (dialog.open) successTitle.focus();
      }
    } finally {
      submitting = false;
      submit.disabled = false;
      channelInputs.forEach(input => { input.disabled = false; });
      form.removeAttribute('aria-busy');
      setButtonLoading(submit, false);
      syncChannel({ clearStatus: false });
      if (!dialog.open) reset();
    }
  });

  cancelForm?.addEventListener('submit', async (event) => {
    event.preventDefault();

    if (!managedSubscription) {
      syncSubscribedTriggers(hasStoredSubscription());
      close();
      return;
    }

    cancelStatus.hidden = true;
    cancelStatus.textContent = '';
    setButtonLoading(cancelSubmit, true);

    try {
      const accountManaged = ACCOUNT_SUBSCRIPTION_ID_PATTERN.test(
        managedSubscription.subscriptionId || '',
      );
      let response;
      if (accountManaged) {
        const endpoint = sameOriginUrl(accountCancelUrl);
        if (!endpoint) {
          throw new Error('Unable to cancel this alert. Please try again.');
        }
        response = await fetch(endpoint.toString(), {
          method: 'POST',
          body: JSON.stringify({
            product_id: productInput.value,
            subscription_id: managedSubscription.subscriptionId,
          }),
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });
      } else {
        if (
          !cancellationTokenInput
          || !CANCELLATION_TOKEN_PATTERN.test(managedSubscription.cancellationToken || '')
        ) {
          throw new Error('Unable to verify this alert. Please refresh the page.');
        }
        cancellationTokenInput.value = managedSubscription.cancellationToken;
        response = await fetch(cancelForm.action, {
          method: 'POST',
          body: new FormData(cancelForm),
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
      }
      const payload = await parseJson(response);
      if (!response.ok || payload.success !== true) {
        throw new Error(payload.message || 'Unable to cancel this alert. Please try again.');
      }

      const cancelledSubscription = managedSubscription;
      clearStoredSubscription(subscriptionIdentity(cancelledSubscription));
      syncSubscribedTriggers(hasStoredSubscription());
      cancelView.dataset.stockAlertCancelled = 'true';
      cancelPrompt.hidden = true;
      cancelConfirmation.hidden = false;
      keepLabel.textContent = 'Back to product';
      if (cancelledTitle) {
        cancelledTitle.textContent = cancelledSubscription.channel === 'sms'
          ? 'Text alert cancelled'
          : cancelledSubscription.channel === 'email'
            ? 'Email alert cancelled'
            : 'Alert cancelled';
      }
      dialog.setAttribute('aria-labelledby', cancelledTitle.id);
      dialog.setAttribute('aria-describedby', cancelledDescription.id);
      setMeasuredHeights({
        cardHeight: constrainedCardHeight(cancelView.scrollHeight),
        animate: true,
      });
      cancelledTitle.focus();
    } catch (error) {
      cancelStatus.textContent = error.message || 'Unable to cancel this alert. Please try again.';
      cancelStatus.hidden = false;
      setMeasuredHeights({
        cardHeight: constrainedCardHeight(cancelView.scrollHeight),
        animate: true,
      });
      cancelStatus.focus();
    } finally {
      setButtonLoading(cancelSubmit, false);
    }
  });

  const addButton = document.querySelector('[data-add-to-bag]');

  if (addButton) {
    const productRoot = addButton.closest('[data-product-section]');
    const syncCoffeeTrigger = () => {
      const isAvailable = addButton.dataset.availability === 'available';
      selectedVariantAvailable = isAvailable;
      syncSubscribedTriggers();
      syncVariant();
    };

    new MutationObserver(syncCoffeeTrigger).observe(addButton, {
      attributes: true,
      attributeFilter: ['data-availability'],
    });
    productRoot?.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-input], [data-variant-select]')) {
        syncCoffeeTrigger();
      }
    });
    syncCoffeeTrigger();
  }

  const equipmentSelect = document.querySelector('[data-stock-alert-variant-select]');

  if (equipmentSelect) {
    const variants = parseEquipmentVariants();
    const equipmentAddButton = document.querySelector('[data-stock-alert-add-button]');
    const equipmentAddButtonText = document.querySelector('[data-stock-alert-add-button-text]');
    const syncEquipmentTrigger = () => {
      const variant = variants.find((candidate) => String(candidate.id) === equipmentSelect.value);
      const isAvailable = Boolean(variant?.available);
      selectedVariantAvailable = isAvailable;
      syncSubscribedTriggers();
      if (equipmentAddButton) {
        equipmentAddButton.disabled = !isAvailable;
      }
      if (equipmentAddButtonText) {
        equipmentAddButtonText.textContent = isAvailable ? 'Add to bag' : 'Sold out';
      }
      syncVariant();
    };

    equipmentSelect.addEventListener('change', syncEquipmentTrigger);
    syncEquipmentTrigger();
  }

  window.addEventListener('pageshow', () => {
    void syncAccountSubscriptions({ force: true });
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void syncAccountSubscriptions();
    }
  });
  window.addEventListener('storage', (event) => {
    if (event.key !== subscriptionStorageKey) {
      return;
    }
    if (accountSyncEnabled) {
      void syncAccountSubscriptions({ force: true });
      return;
    }
    currentSubscriptions = readStoredSubscription() || [];
    managedSubscription = currentSubscriptions[0] || null;
    syncSubscribedTriggers();
    reconcileOpenAccountView();
  });

  syncSubscribedTriggers();
  triggers.forEach((trigger) => { trigger.dataset.stockAlertReady = 'true'; });
  void syncAccountSubscriptions({ force: true });
};

if (!window[CONTROLLER_KEY]) {
  window[CONTROLLER_KEY] = true;
  document.querySelectorAll(DIALOG_SELECTOR).forEach(initializeStockAlert);
}
